// Vendor boundary for product analytics.
//
// This is the ONLY module that imports the analytics SDK. Everything else in the
// app talks to analytics through `@/lib/tracking` (init/identify/track/…), so
// swapping vendors later is a change confined to this file.
//
// Capture strategy: MANUAL-first. Noisy DOM autocapture is disabled; every vital
// action is tracked explicitly via `track()` using the names in `events.js`.
import posthog from 'posthog-js'

// PostHog project API keys are public by design (they ship in the client
// bundle). Overridable per environment via VUE_APP_POSTHOG_API_KEY / _HOST.
const API_KEY =
	process.env.VUE_APP_POSTHOG_API_KEY ||
	'phc_IsUhJUJNi6ym3wGmh1S6Pb00kFiU2cq8oZlwymezT8c'
const API_HOST =
	process.env.VUE_APP_POSTHOG_HOST || 'https://dt.castr.com'

const DEBUG = process.env.VUE_APP_API_ENV !== 'prod'

let initialized = false

export function initAnalytics() {
	if (initialized || typeof window === 'undefined' || !API_KEY) return
	try {
		posthog.init(API_KEY, {
			api_host: API_HOST,
			ui_host: 'https://us.posthog.com',
			defaults: '2026-05-30',
			// Manual-first: the app fires its own 'Page Viewed' events, so disable
			// PostHog's auto pageview (avoids double counting) and DOM autocapture.
			autocapture: false,
			capture_pageview: false,
			// Session replay disabled (mirrors prior config; FullStory also records).
			disable_session_recording: true,
			// Only create person profiles for identified users (the app identifies
			// on login). Tradeoff: anonymous pre-login events won't create person
			// profiles, which keeps MTU/cost down.
			person_profiles: 'identified_only',
		})
		// Super properties: attached to every subsequent event (replaces the old
		// enrichment plugin).
		posthog.register({ app_env: process.env.VUE_APP_API_ENV || 'unknown' })
		// Non-prod builds only: console access, and the Playwright specs wrap
		// `capture` here because PostHog drops events from automated browsers.
		if (DEBUG) window.posthog = posthog
		initialized = true
	} catch (e) {
		if (DEBUG) console.warn('[analytics] init failed', e)
	}
}

// Merge properties that should ride along on every subsequent event.
export function setCommonProps(props = {}) {
	if (!initialized) initAnalytics()
	if (!initialized) return
	try {
		posthog.register(props || {})
	} catch (e) {
		if (DEBUG) console.warn('[analytics] setCommonProps failed', e)
	}
}

function deriveUserProps(user) {
	const subs = Array.isArray(user?.subscriptions) ? user.subscriptions : []
	const liveSub = subs.find(
		(s) => s?.category && String(s.category).includes('live'),
	)
	const planName = liveSub?.package?.name || 'Free'
	return {
		plan: planName,
		plan_category: liveSub?.category || 'none',
		is_trial: String(planName).toLowerCase().includes('trial'),
		is_team_member: !!user?.parentAccount,
		subscription_count: subs.length,
	}
}

// Resolve the canonical user id. We prefer the aMember id (`cmsEntityId`) so
// off-app billing events (aMember/Stripe purchases) attribute to the same
// profile with no cross-system mapping; fall back to the Castr Mongo `_id` for
// the rare user that has no cmsEntityId yet.
export function resolveUserId(user) {
	if (!user) return ''
	return user.cmsEntityId ? String(user.cmsEntityId) : user._id || ''
}

// Tie the current session to a user + set person properties. Call on login and
// signup. Segmentation-critical props are mirrored onto every event too.
export function identifyUser(user) {
	if (!initialized) initAnalytics()
	if (!initialized || !user || !user._id) return
	try {
		const derived = deriveUserProps(user)
		const userProps = { ...derived }
		if (user.email) {
			userProps.email = user.email
			userProps.$email = user.email
		}
		if (user.name) {
			userProps.name = user.name
			userProps.$name = user.name
		}
		// Keep BOTH ids as properties so either system can cross-reference.
		userProps.castr_user_id = user._id
		if (user.cmsEntityId) userProps.cms_entity_id = String(user.cmsEntityId)
		if (user.joinDate) userProps.signup_date = user.joinDate
		posthog.identify(resolveUserId(user), userProps)
		setCommonProps(derived)
	} catch (e) {
		if (DEBUG) console.warn('[analytics] identify failed', e)
	}
}

// Clear identity on logout so the next user starts a fresh anonymous session.
export function resetAnalytics() {
	try {
		posthog.reset()
	} catch (e) {
		/* noop */
	}
	try {
		posthog.register({ app_env: process.env.VUE_APP_API_ENV || 'unknown' })
	} catch (e) {
		/* noop */
	}
}

// Track a named event. `name` should come from EVENTS in `events.js`.
export function track(name, props = {}) {
	if (!initialized) initAnalytics()
	if (!initialized || !name) return
	try {
		posthog.capture(name, props || {})
		if (DEBUG) console.debug('[analytics] track:', name, props)
	} catch (e) {
		if (DEBUG) console.warn('[analytics] track failed', name, e)
	}
}

// Current analytics identity, for stitching off-app flows (e.g. the aMember/
// Stripe checkout on app.castr.com). Pass these through the checkout redirect so
// the purchase event can be attributed to the same user + session.
export function getAnalyticsIdentity() {
	try {
		return {
			distinctId: posthog.get_distinct_id?.() || '',
			sessionId: posthog.get_session_id?.() || '',
		}
	} catch (e) {
		return { distinctId: '', sessionId: '' }
	}
}

// Revenue tracking for money-moment events (checkout, upgrades). PostHog has no
// dedicated revenue object, so we emit a normal event whose properties carry the
// amount/currency/product so downstream analysis can sum them.
export function trackRevenue({
	productId,
	price,
	quantity = 1,
	revenueType,
	props,
} = {}) {
	if (!initialized) initAnalytics()
	if (!initialized) return
	try {
		const revenue =
			typeof price === 'number' ? price * (quantity || 1) : undefined
		posthog.capture('Revenue', {
			productId,
			price,
			quantity,
			revenueType,
			revenue,
			...(props || {}),
		})
	} catch (e) {
		if (DEBUG) console.warn('[analytics] revenue failed', e)
	}
}

// Shared singleton for the usePostHog composable — ensures init happened and
// returns the SAME initialized instance (no double init).
export function getPosthog() {
	if (!initialized) initAnalytics()
	return posthog
}
